import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { History, Loader2, RefreshCw } from "lucide-react";

const ACTIONS = {
  login: { label: "Signed in", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  logout: { label: "Signed out", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  login_failed: { label: "Failed sign-in", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  login_blocked: { label: "Blocked (locked)", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  account_locked: { label: "Account locked", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  account_auto_unlocked: { label: "Auto-unlocked", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  device_approval_requested: { label: "New device", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  device_auto_approved: { label: "Device auto-approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  lead_created: { label: "Lead created", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  lead_updated: { label: "Lead edited", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  lead_status_changed: { label: "Status changed", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  lead_deleted: { label: "Lead deleted", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  report_downloaded: { label: "Report download", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  manager_remark_added: { label: "Manager remark", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "medium" });

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_email: "all", action: "all", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
      const { data } = await api.get("/admin/activity", { params });
      setLogs(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="activity-log-panel">
      <div className="px-5 py-4 border-b border-slate-100 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <History className="h-4 w-4 text-[#1E3A8A]" /> Activity Log
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Sign-ins, lead changes and report downloads across all users</p>
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="activity-refresh-button">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filters.user_email} onValueChange={(v) => set("user_email", v)}>
            <SelectTrigger className="w-52 h-9" data-testid="activity-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.email}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.action} onValueChange={(v) => set("action", v)}>
            <SelectTrigger className="w-48 h-9" data-testid="activity-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All activities</SelectItem>
              {Object.entries(ACTIONS).map(([k, a]) => <SelectItem key={k} value={k}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" className="w-40 h-9" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="activity-date-from" />
          <Input type="date" className="w-40 h-9" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="activity-date-to" />
        </div>
      </div>
      {loading ? (
        <div className="py-12 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : logs.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400" data-testid="activity-empty">No activity found</p>
      ) : (
        <div className="overflow-x-auto max-h-[600px]">
          <Table data-testid="activity-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l, i) => {
                const a = ACTIONS[l.action] || { label: l.action, cls: "" };
                return (
                  <TableRow key={`${l.timestamp}-${i}`} data-testid="activity-row">
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmt(l.timestamp)}</TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{l.user_name || "Unknown"}</p>
                      <p className="text-xs text-slate-500 font-mono">{l.user_email}</p>
                    </TableCell>
                    <TableCell><Badge variant="outline" className={a.cls}>{a.label}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{l.details || "—"}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{l.ip || "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
